import React, { useState, useEffect } from "react";
import Images from "../images";
import { Link, useNavigate } from "react-router-dom";
import Signup from "./Signup";
import SignIn from "./SignIn";
import ResetPasswordModal from "./ResetPassword";
import { Carousel, Modal } from "react-bootstrap";
// Bundled (content-hashed) so CDN caching of public/assets/css/style.css can't hide changes
import "./Home.css";

// Helper: derive a readable alt from the filename when explicit alt isn't provided
const deriveAltFromSrc = (src) => {
  if (!src) return "image";
  try {
    const parts = src.split("/").pop().split("?")[0].split("#")[0];
    const name = parts.replace(/\.[^/.]+$/, "").replace(/[-_\.]/g, " ");
    const cleaned = name.replace(/\s+/g, " ").trim();
    // Title-case short alt
    return cleaned.replace(/(^\w)|\s(\w)/g, (m) => m.toUpperCase());
  } catch (e) {
    return "image";
  }
};

// Reusable lazy-loading image component with sensible defaults
const LazyImg = ({
  src,
  alt,
  className,
  loading = "lazy",
  width,
  height,
  srcSet,
  sizes,
  style,
  onLoad,
  ...props
}) => (
  <img
    src={src}
    alt={alt || deriveAltFromSrc(src)}
    className={className}
    loading={loading}
    width={width}
    height={height}
    srcSet={srcSet}
    sizes={sizes}
    style={style}
    onLoad={onLoad}
    {...props}
  />
);

// Counts up from 0 to the numeric part of `value` (e.g. "12,000+") once it scrolls into view
const CountUp = ({ value, duration = 1500 }) => {
  const target = parseInt(value.replace(/[^\d]/g, ""), 10) || 0;
  const suffix = value.replace(/[\d,]/g, "");
  const ref = React.useRef(null);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (
      !("IntersectionObserver" in window) ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      setCurrent(target);
      return;
    }
    let frame;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        observer.disconnect();
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min((now - start) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 3);
          setCurrent(Math.round(target * eased));
          if (progress < 1) frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
      },
      { threshold: 0.4 }
    );
    observer.observe(el);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [target, duration]);

  return (
    <span ref={ref} aria-label={value}>
      <span aria-hidden="true">
        {current.toLocaleString("en-IN")}
        {suffix}
      </span>
    </span>
  );
};

const Home = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [showCount, setShowCount] = useState(4);
  const [isPlaying, setIsPlaying] = useState(false);
  const [activeModal, setActiveModal] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [showSignIn, setShowSignIn] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [showMp4, setShowMp4] = useState(false);
  const [videoLoading, setVideoLoading] = useState(true);
  const handlePlay = () => {
    setIsPlaying(true);
  };
  useEffect(() => {
    const checkAuth = () => {
      const token = localStorage.getItem("token");
      setIsAuthenticated(!!token); // true if token exists
    };

    // Check on initial load
    checkAuth();

    // Listen for storage events (including our custom one)
    const handleStorageChange = () => {
      checkAuth();
    };

    window.addEventListener("storage", handleStorageChange);

    // Add this custom event listener for logout events
    window.addEventListener("auth-change", handleStorageChange);

    // Clean up event listeners
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      window.removeEventListener("auth-change", handleStorageChange);
    };
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    setShowSignIn(false);
    setShowSignup(false);
  };

  const handleOpen = (e) => {
    e.preventDefault(); // Prevent default link behavior
    setShowVideo(true);
  };

  const handleClose = () => {
    setShowVideo(false);
  };
  const handleMp4Open = (e) => {
    e.preventDefault(); // Prevent default link behavior
    setShowMp4(true);
  };

  const handleMp4Close = () => {
    setShowMp4(false);
  };
  const [loading, setLoading] = useState(true);

  // ✅ useEffect instead of React.useEffect
  useEffect(() => {
    const timer = setTimeout(() => {
      setLoading(false);
    }, 300); // loader disappears after 2s

    return () => clearTimeout(timer);
  }, []);

  const teamMembers = [
    {
      name: "Dr. Meenal Hastak",
      img: Images.Meena,
      desc: "Senior Consultant Surgical Pathologist",
      moreInfo: [
        "Works at Metropolis Healthcare, Asian Cancer Institute, and Gleneagles Hospital, Mumbai.",
        "4+ decades of teaching experience in GMC and JJ Hospital, Mumbai.",
        "Corporate experience in Kokilaben Dhirubhai Ambani Hospital and Lilavati Hospital.",
        "MD & DNB teacher; mentors next generation of pathologists.",
      ],
    },
    {
      name: "Dr. Pallavi Purwar",
      img: Images.Pallavi,
      desc: "Thoracic Surgeon, Delhi",
      moreInfo: [
        "Surgeon of lungs and food pipe.",
        "Focus on lung health awareness and healthcare cost optimization.",
        "Runs Delhi Chest Surgery Clinic and hospital-based practice.",
        "Expertise in Lung Cancer, Bronchiectasis, Aspergilloma, Tuberculosis, Tracheal Stenosis, Esophageal Tumors, Thymoma, Pleural Effusion, Empyema, Lung Abscess.",
        "Worked with NGOs like Ganga Prem Hospice and Sri Sai Onkar Trust.",
      ],
    },
    {
      name: "Dr. Praveen Gupta",
      img: Images.Praveen,
      desc: "Director, Laboratory Services, Primus Super Speciality Hospital",
      moreInfo: [
        "Heads Laboratory Medicine & Transfusion Medicine at NABL & NABH accredited hospital.",
        "Established 'One Health Lab' at SKMCH Muzaffarpur and NGS lab collaboration with DFY & TMH Hospital.",
        "Extensive experience in lab operations, PCR, ELISA, blood gas analyzers.",
        "MD in Laboratory Medicine from AIIMS, New Delhi; senior residency at AIIMS.",
        "Former Grade A Medical Officer with ESI, Ranchi.",
      ],
    },
    {
      name: "Dr. P. K Mohanty",
      img: Images.Mohanty,
      desc: "Professor of Biochemistry",
      moreInfo: [
        "Passionate about teaching & training medical students in lab medicine.",
        "Academic administrator responsible for curriculum planning and faculty development.",
        "Guides research at undergraduate and postgraduate levels.",
      ],
    },
    {
      name: "Dr. Divya Wadhawan",
      img: Images.Divya,
      desc: "Senior Consultant Gynaecologist & Obstetrician",
      moreInfo: [
        "15+ years experience, special interests in laparoscopy and infertility.",
        "Consultant at MCD hospital, now at Apollo Cradle Royale Hospital.",
        "Published research papers and presentations.",
        "TEDx speaker, 2023.",
        "Artist and NGO supporter for girl child upliftment.",
      ],
    },
    {
      name: "Dr. Rajat Jain",
      img: Images.Rajat,
      desc: "President, Doctors For You (DFY)",
      moreInfo: [
        "Leader in medical and humanitarian fields.",
        "Supported public health, disaster management, and humanitarian aid.",
        "Crucial role during COVID-19 pandemic across India.",
      ],
    },
    {
      name: "Dr. Ravi Kant Singh",
      img: Images.Ravi,
      desc: "Officer In-charge, Homi Bhabha Cancer Hospital & Associate Professor, Tata Memorial Centre",
      moreInfo: [
        "Developed public health systems for NCD, TB, AMR, One Health & Emergency Preparedness.",
        "Established Oral, Breast & Cervical cancer screening programs in Assam and Bihar.",
        "First & Only Indian to receive SAARC award for disaster response (2010).",
      ],
    },
    {
      name: "Dr. Anuradha Murthy",
      img: Images.Anuradha,
      desc: "Senior Consultant Surgical Pathologist, Metropolis Healthcare",
      moreInfo: [
        "Helped start the Surgical Pathology unit in 2003.",
        "Laboratory is NABL & CAP accredited, using advanced techniques.",
        "Passionate about History of Medicine; created Pathology Museum at Bombay Hospital in 1994.",
      ],
    },
    {
      name: "Dr. Arvind Krishnamurthy",
      img: Images.ArvindKrishnamurthy,
      desc: "Professor & Head of Surgical Oncology, Cancer Institute (WIA), Chennai",
      moreInfo: [
        "Super Specialty training from Tata Memorial Center, Mumbai.",
        "Clinical interests: Thoracic, Head/Neck, and Breast Oncology.",
        "Involved in multicenter clinical trials and research.",
        "185+ publications and 6 book chapters.",
        "Awards: Dr. KS Sanjivi Award, Best Doctors Award, Iyan Valluvar award, Sushruta Award.",
      ],
    },
    {
      name: "Dr. Neelam Mohan",
      img: Images.NeelamMohan,
      desc: "Pioneer in Pediatric Hepatology & Liver Transplantation",
      moreInfo: [
        "Established the busiest Paediatric Liver Transplant Programme in India.",
        "Recipient of Dr. B.C. National Award by President of India (2016).",
        "Formulated Global Guidelines for Hepatitis B & C management in children.",
        "National coordinator of Diarrhea Modules 2018-2019 with IAP.",
      ],
    },
    {
      name: "Dr. Shanaz Khodaiji",
      img: Images.ShanazKhodaiji,
      desc: "Consultant Hematology & Transfusion Medicine, P.D. Hinduja Hospital",
      moreInfo: [
        "MBBS from AFMC Pune, DCP & MD (Path) from KGMC Lucknow.",
        "40+ years of experience in laboratory hematology.",
        "Trained internationally in flow cytometry, cytogenetics, and molecular diagnostics.",
        "Established BD-PBHNH Centre of Excellence in clinical flow cytometry.",
        "Principal investigator in research projects and DNB dissertations.",
      ],
    },
    {
      name: "Dr. (Col). Jaswinder Kaur Bhatia",
      img: Images.JaswinderKaur,
      desc: "Pathologist & Post Graduate Teacher",
      moreInfo: [
        "MBBS from Armed Forces Medical College, Pune.",
        "27 years of experience in pathology.",
        "Postgraduate teacher at Pune University, MUHS, and WBUHS.",
        "Published 30+ papers.",
        "Awards: Chief of Army Staff Commendation, Best Paper award (MJAFI).",
      ],
    },
    {
      name: "Dr. Meena Desai",
      img: Images.MeenaDesai,
      desc: "Chief Medical Officer & Consultant Histopathologist",
      moreInfo: [
        "Completed MBBS and M.D.(Pathology) from Seth G. S. Medical College and KEM Hospital, Mumbai.",
        "Head of Dept. of Laboratory Medicine and Consultant Histopathologist & Cytopathologist at SevenHills Hospital, Mumbai.",
        "Assoc. Prof. of Pathology at Seth G.S. Medical College & K.E.M. Hospital.",
        "Consultant Pathologist & In-charge Dept. of Pathology & Transfusion Medicine at Lilavati Hospital & Research Centre.",
        "Consultant Surgical Pathologist at Prince Aly Khan Hospital, Guru Nanak Hospital, and Metropolis Reference Laboratory, Mumbai.",
      ],
    },
    {
      name: "Dr. Rajiv Yadav",
      img: Images.RajivYadav,
      desc: "Chief Operating Officer, Asian Institute of Oncology",
      moreInfo: [
        "Over 17 years of experience in Hospital Management, P&L Ownership, Medical Consultancy, Quality & People Management.",
        "Expert in developing and executing hospital administration strategies and long-term growth plans.",
        "Directed clinical & non-clinical operations including Patient Care, Medical & Technological Advancement, and Sanitization.",
      ],
    },
    {
      name: "Dr. Pradeep Vaideeshwar",
      img: Images.Pradeep,
      desc: "Professor, Department of Pathology (Cardiovascular & Thoracic Division)",
      moreInfo: [
        "Specialised in Cardiovascular & Pulmonary/Thoracic pathology.",
        "Visiting Clinician in Pulmonary Pathology at the Mayo Clinic, USA.",
        "Authored 100+ platform presentations and published 200+ papers.",
        "President of the Indian Association of Pathologists & Microbiologists.",
      ],
    },
    {
      name: "Dr. (Capt) Prateek Kinra",
      img: Images.Pratik,
      desc: "Human Resources Director, Government of India",
      moreInfo: [
        "Experienced Pathologist with expertise in Gastrointestinal and molecular pathology, cytology, medical education, and Immunohistochemistry.",
        "Post-doctoral fellowship in GI Pathology from AIIMS.",
        "Strong healthcare services professional with leadership experience.",
      ],
    },
    {
      name: "Dr. Vivek Talaulikar",
      img: Images.Vivek,
      desc: "CEO, Gleneagles Global Hospital, Mumbai",
      moreInfo: [
        "Experience in Hospital Operations, Strategic Leadership, and Business Development.",
        "Led teams across Apollo, Fortis, Sterling Hospitals, and Gleneagles Global.",
        "Instrumental in heart, lung, and hand transplant program development.",
        "MBBS from Goa Medical College; Masters in Hospital Administration from Osmania University.",
      ],
    },
  ];
  const bannerImages = [
    {
      src: Images.Banner1,
      alt: "Banner Image 1",
    },
    {
      src: Images.Banner2, // Add more images as needed
      alt: "Banner Image 2",
    },
    {
      src: Images.Banner3,
      alt: "Banner Image 3",
    },
    {
      src: Images.Banner4,
      alt: "Banner Image 4",
    },
  ];
const APPLICATIONS_OPEN = new Date("2026-10-21T00:00:00+05:30");

const heroDates = [
  {
    label: "Applications open",
    shortLabel: "Opens",
    value: "21st October 2026",
    shortValue: "21 Oct 2026",
    icon: "fa fa-calendar",
  },
  {
    label: "Last date",
    shortLabel: "Last date",
    value: "31st December 2026",
    shortValue: "31 Dec 2026",
    icon: "fa fa-hourglass-end",
  },
  {
    label: "Results",
    shortLabel: "Results",
    value: "February 2027",
    shortValue: "Feb 2027",
    icon: "fa fa-trophy",
  },
];

// TODO: bracketed figures in the brief ([12,000+], [1,500+], [193]) await final confirmation
const impactStats = [
  { value: "12,000+", label: "Registrations", icon: "fa fa-user-plus" },
  { value: "500+", label: "Medical colleges reached", icon: "fa fa-university" },
  { value: "1,500+", label: "Scholarships awarded", icon: "fa fa-graduation-cap" },
  { value: "193", label: "Research grants", icon: "fa fa-file-text-o" },
  { value: "4,500+", label: "Alumni", icon: "fa fa-users" },
  { value: "200+", label: "Metropolis laboratories open for observerships", icon: "fa fa-flask" },
];

// TODO: replace stock card images with Metropolis lab photos when available
const programmes = [
  {
    title: "Scholarship",
    img: Images.Homescholor,
    descr:
      "Merit-cum-need scholarships for MBBS students, interns and MD/MS/DNB trainees, with a leading consulting firm as the process partner.",
    to: "/scholarship-program",
    cta: "Explore scholarships",
  },
  {
    title: "Academic Research Grant",
    img: Images.Homeacademic,
    descr:
      "Funding and mentorship for student-led research, assessed by an independent jury of senior clinicians and academics.",
    to: "/academic-research-support",
    cta: "Explore research grants",
  },
  {
    title: "Observership",
    img: Images.Homeobserver,
    descr: "Hands-on exposure at 200+ Metropolis laboratories across India.",
    to: "/observership-program",
    cta: "Explore observerships",
  },
  {
    title: "Laboratory Tour",
    img: Images.Homelab,
    descr:
      "See how a high-volume, accredited laboratory works, from sample collection to final report.",
    to: "/laboratory-tour",
    cta: "Explore laboratory tours",
  },
  {
    title: "Internship Research Programme",
    img: Images.Homeintern,
    descr:
      "Structured research internships alongside Metropolis scientists and pathologists.",
    to: "/internship-program",
    cta: "Explore internships",
  },
  {
    title: "MedTalk and Knowledge Community",
    img: Images.Homebuilding,
    descr:
      "Webinars and conversations with experienced doctors, and a growing alumni network.",
    to: "/building-knowledge-community",
    cta: "Explore MedTalk",
  },
];

const howItWorks = [
  "Register on med-engage.com",
  "Apply with your academic record and documents; research grant applicants add a proposal",
  "Scholarships Evaluation on merit and need, managed by a leading consulting firm as a process partner; research proposals reviewed by an expert jury",
  // TODO: add the Summit month once confirmed
  "Results and felicitation at the MedEngage Summit 2027",
];

// TODO: quote text and photos will be shared by Metropolis once applications are live
const quotes = [
  { name: "Chairperson", role: "Metropolis Healthcare Limited", quote: "[Quote to be shared]" },
  { name: "Managing Director", role: "Metropolis Healthcare Limited", quote: "[Quote to be shared]" },
  { name: "Dr. Kirti Chadha", role: "Chief Scientific & Innovation Officer, Metropolis Healthcare Limited", quote: "[Quote to be shared]" },
  { name: "Jury member", role: "MedEngage Jury", quote: "[Quote to be shared]" },
  { name: "Anshika Agarwal", role: "Lady Hardinge Medical College", quote: "[Quote to be shared]" },
  { name: "Alumnus", role: "MedEngage alumni", quote: "[Quote to be shared]" },
];

// TODO: award images will be shared once applications are live
const awards = [
  {
    title: "ASSOCHAM, Best CSR Excellence in Healthcare",
    meta: "New Delhi, 22 August 2023",
  },
  {
    title: "NATHealth (Healthcare Federation of India), recognition for MedEngage under CSR",
    meta: "New Delhi, 21 March 2024",
  },
];

const pillars = [
  {
    icon: "fa fa-graduation-cap",
    title: "Youth",
    descr: "Support from MBBS through to postgraduate training",
  },
  {
    icon: "fa fa-balance-scale",
    title: "Equity",
    descr: "Priority for students from Aspirational Districts",
  },
  {
    icon: "fa fa-female",
    title: "Women in medicine",
    descr: "Strong representation of women among our scholars",
  },
];

const alignments = ["NMC CBME", "NEP 2020", "Ayushman Bharat", "National Health Mission"];

// Official UN SDG colours
const sdgs = [
  { num: 3, color: "#4C9F38" },
  { num: 4, color: "#C5192D" },
  { num: 5, color: "#FF3A21" },
  { num: 10, color: "#DD1367" },
];
// Inside the component, before return:
const applicationsOpen = new Date() >= APPLICATIONS_OPEN;
  useEffect(() => {
    document.title = "Metropolis Foundation CSR - MedEngage";
  }, []);

  return (
    <>
      {loading && (
        <div
          className="fixed-top w-100 h-100 d-flex justify-content-center align-items-center bg-dark bg-opacity-50"
          style={{ zIndex: 9999 }}
        >
          <div className="custom-spinner"></div>
        </div>
      )}
      <div>
        {/* HEADER SECTION START */}

        {/* HEADER SECTION END */}

        <main className="overflow-hidden">
          {/* BANNER SECTION START */}

        <section id="home" className="ul-banner">
    <div className="ul-banner-container">
      <div className="row gy-4 row-cols-lg-2 row-cols-1 align-items-center flex-column-reverse flex-lg-row">
        {/* Banner text */}
        <div className="col">
          <div className="ul-banner-txt">
            <div className="wow animate__fadeInUp">
              <div className="text-start">
                <p className="hero-edition">
                  MedEngage 2026-27{" "}
                  <span className="hero-edition-sep" aria-hidden="true">|</span>{" "}
                  <span className="hero-edition-num">9th Edition</span>
                </p>
                {applicationsOpen && (
                  <span className="hero-call-badge">
                    Call for applications open
                  </span>
                )}

                <h1 className="ul-banner-title m-1">
                  Nurturing India's next generation of doctors
                </h1>

                <p className="ul-banner-descr text-light">
                  Scholarships, research grants and laboratory-based learning
                  for MBBS students, interns and MD/MS/DNB trainees across
                  India.
                </p>

                <div className="ul-banner-btns mt-3">
                  {applicationsOpen ? (
                    <button
                      className="btn btn-light text-success py-2 px-4 rounded-3 fw-medium"
                      onClick={() => navigate("/scholarship-program")}
                    >
                      Apply now{" "}
                      <i className="fa fa-arrow-right ms-2" aria-hidden="true"></i>
                    </button>
                  ) : (
                   <div className="d-flex flex-column align-items-start gap-3">
  <p className="hero-open-note mb-0 text-light fw-medium">
    Applications open on <strong className="fw-bold">21st October 2026</strong>
  </p>
  <a
    href="#subscribe"
    className="btn btn-light text-success py-2 px-4 rounded-3 fw-medium"
  >
    Notify me
  </a>
</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Banner image – unchanged */}
        <div className="col align-self-start">
          <div className="ul-banner-img">
            <Carousel
              fade
              interval={2000}
              controls={false}
              indicators={false}
              pause={false}
              className="banner-carousel"
            >
              {bannerImages.map((image, index) => (
                <Carousel.Item key={index}>
                  <div className="img-wrapper carousel-img-wrapper">
                    <LazyImg
                      src={image.src}
                      alt={`${image.alt || "MedEngage banner"}${
                        image.title ? " - " + image.title : ""
                      }`}
                      className="img-fluid carousel-banner-img"
                      loading={index === 0 ? "eager" : "lazy"}
                      fetchpriority={index === 0 ? "high" : undefined}
                      onLoad={(e) => {
                        e.target.style.opacity = "1";
                      }}
                      style={{ opacity: 0, transition: "opacity 0.3s ease" }}
                      width={1200}
                      height={600}
                    />
                  </div>
                </Carousel.Item>
              ))}
            </Carousel>
          </div>
        </div>
      </div>
    </div>
  </section>
 {/* Key dates strip – white band under the hero */}
 <div className="hero-dates-band">
  <div className="container">
    <dl className="hero-dates">
{heroDates.map(({ label, shortLabel, value, shortValue, icon }) => (
  <div className="hero-date-item" key={label}>
    <span className="hero-date-icon">
      <i className={icon} aria-hidden="true"></i>
    </span>
    <div className="hero-date-text">
      <dt className="hero-dates-label">
        <span className="d-md-none">{shortLabel}</span>
        <span className="d-none d-md-inline">{label}</span>
      </dt>
      <dd className="hero-dates-value">
        <span className="d-md-none">{shortValue}</span>
        <span className="d-none d-md-inline">{value}</span>
      </dd>
    </div>
  </div>
))}
    </dl>
  </div>
</div>
          {/* BANNER SECTION END */}

          {/* IMPACT SECTION START */}
          <section className="impact-section" aria-labelledby="impact-title">
            <div className="container">
              <h2 id="impact-title" className="impact-title text-center">
                MedEngage since 2017
              </h2>
              <div className="row row-cols-2 row-cols-lg-6 g-3 g-lg-4 text-center">
                {impactStats.map(({ value, label, icon }) => (
                  <div className="col" key={label}>
                    <div className="impact-stat">
                      <span className="impact-icon" aria-hidden="true">
                        <i className={icon}></i>
                      </span>
                      <span className="impact-value">
                        <CountUp value={value} />
                      </span>
                      <span className="impact-label">{label}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          {/* IMPACT SECTION END */}

          {/* ABOUT SECTION START */}
          <section className="ul-about ul-section-spacing wow animate__fadeInUp">
            <div className="ul-container">
              <div className="row row-cols-md-2 row-cols-1 align-items-center gy-3 ul-about-row">
                <div className="col">
                  <div className="ul-about-txt">
                    <h2 className="ul-section-title " id="about">
                      About <strong className="green ul-section-title">MedEngage</strong>
                    </h2>
                    <p className="ul-section-descr">
                      Conceived in 2017 by our Founder{" "}
                      <strong>Dr Sushil Shah</strong>, MedEngage is the{" "}
                      <strong>flagship CSR initiative</strong> of{" "}
                      <strong className="semi">
                        Metropolis Healthcare Limited
                      </strong>
                      . It has grown into one of India's largest private-sector
                      medical outreach programmes.
                    </p>
                    <p className="ul-section-descr">
                      We combine financial support with access to Metropolis's
                      laboratory network and scientific expertise, so that
                      talented medical students can learn, research and grow,
                      whatever their background.
                    </p>
                  </div>
                </div>
                <div className="col position-relative">
                  <LazyImg
                    src={Images.InternImage}
                    alt="Medical interns observing laboratory procedures during MedEngage training"
                    className="img-fluid rounded shadow"
                    loading="lazy"
                    width={450}
                    height={250}
                  />

                  <LazyImg
                    src={Images.LabImage}
                    alt="Medical students performing practical exercises in a clinical laboratory"
                    className="img-fluid rounded shadow position-absolute intern-image"
                    loading="lazy"
                    width={350}
                    height={200}
                  />
                </div>
              </div>
            </div>
          </section>
          {/* ABOUT SECTION END */}

          {/* PROGRAMMES SECTION START */}
          <section id="programs" className="ul-events ul-section-spacing">
            <div className="ul-container">
              <div className="d-flex align-items-center justify-content-center">
                <h2 className="ul-section-title text-white text-center main-heading">
                  One programme, <strong className="fw-semibold">six ways to grow</strong>
                </h2>
              </div>
              <div className="ul-events-wrapper">
                <div className="row ul-bs-row row-cols-lg-3 row-cols-md-2 row-cols-1 g-4">
                  {programmes.map(({ title, img, descr, to, cta }) => (
                    <div className="col d-flex" key={title}>
                      <div className="ul-event ul-event--stacked w-100">
                        <div className="ul-event-img">
                          <LazyImg
                            src={img}
                            alt={`${title} — MedEngage programme`}
                            loading="lazy"
                            width={400}
                            height={250}
                          />
                        </div>
                        <div className="ul-event-txt">
                          <h3 className="ul-event-title">{title}</h3>
                          <p className="ul-event-descr thumb-p">{descr}</p>
                          <Link to={to} className="green thumb-p fw-medium">
                            {cta} <i className="fa fa-arrow-right" aria-hidden="true"></i>
                          </Link>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </section>
          {/* PROGRAMMES SECTION END */}

          {/* WHO CAN APPLY SECTION START */}
          <section className="apply-section ul-section-spacing" id="eligibility">
            <div className="container">
              <div className="row g-4 g-lg-5 align-items-start">
                <div className="col-lg-4">
                  <h2 className="ul-section-title">
                    Who can <strong className="green">apply</strong>
                  </h2>
                  <p className="ul-section-descr">
                    Students in any year of MBBS, the internship year, or
                    MD/MS/DNB training at a recognised medical college in India,
                    government or private.
                  </p>
                  {/* TODO: point to a dedicated eligibility/FAQ page once it exists */}
                  <Link
                    to="/scholarship-program"
                    className="btn btn-outline-success btn-eligibility rounded-3 fw-medium mt-2 d-block d-sm-inline-block"
                  >
                    Read the full eligibility and FAQs
                  </Link>
                </div>
                <div className="col-lg-8">
                  <h2 className="ul-section-title">
                    How it <strong className="green">works</strong>
                  </h2>
                  <ol className="apply-steps">
                    {howItWorks.map((step, index) => (
                      <li className="apply-step" key={index}>
                        <span className="apply-step-num" aria-hidden="true">
                          {index + 1}
                        </span>
                        <p className="apply-step-text mb-0">{step}</p>
                      </li>
                    ))}
                  </ol>
                </div>
              </div>
            </div>
          </section>
          {/* WHO CAN APPLY SECTION END */}

          {/* OPPORTUNITY SECTION START */}
          <section className="opportunity-band">
            <div className="container">
              <div className="row g-4 align-items-center">
                <div className="col-lg-6">
                  <h2 className="opportunity-title">Opportunity for all</h2>
                  <p className="mb-0 text-light">
                    Talent is everywhere; opportunity is not. MedEngage gives
                    priority to deserving students from NITI Aayog's 112
                    Aspirational Districts and makes dedicated provisions for
                    students from single-parent families and persons with
                    disabilities.
                  </p>
                </div>
                <div className="col-lg-6">
                  <div className="row g-3 text-center">
                    <div className="col-6">
                      <div className="opportunity-stat">
                        <span className="opportunity-value">55%</span>
                        <span className="opportunity-label">
                          of our scholars come from Aspirational Districts
                        </span>
                      </div>
                    </div>
                    <div className="col-6">
                      <div className="opportunity-stat">
                        <span className="opportunity-value">60%</span>
                        <span className="opportunity-label">
                          of our scholars are women
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
          {/* OPPORTUNITY SECTION END */}

          {/* VOICES SECTION START */}
          <div id="voices" className="ul-section-spacing">
            <div className="ul-container">
              <div className="ul-donate-form-section">
                <div className="row justify-content-between align-items-center">
                  <div className="container">
                    <h2 className="text-white text-center voices-title">
                      Voices of MedEngage
                    </h2>
                    <div className="row align-items-center g-4 p-2">
                      <div className="col-lg-6 text-center">
                        <div className="video-container-overlay">
                          {videoLoading && (
                            <div className="video-loader-overlay">
                              <div
                                className="spinner-border text-success"
                                role="status"
                              >
                                <span className="visually-hidden">
                                  Loading...
                                </span>
                              </div>
                            </div>
                          )}
                          <iframe
                            src="https://www.youtube.com/embed/0HqBY5-BpKo"
                            title="A story of Resilience"
                            allowFullScreen
                            className="rounded-3"
                            onLoad={() => setVideoLoading(false)}
                          ></iframe>
                        </div>
                        <p className="fw-600 text-light">
                          A story of Resilience
                        </p>
                      </div>
                      <div className="col-lg-6 text-white">
                        <h3 className="mb-4">
                          Hear from our scholars, mentors and leaders
                        </h3>
                        <div className="row g-1">
                          <>
                            {/* Speaker 1 */}
                            <div className="col-sm-6">
                              <div className="speaker-card text-light h-100 p-3 rounded-3">
                                <div className="position-relative mb-3">
                                  <a href="#" onClick={handleOpen}>
                                    <LazyImg
                                      src="./assets/img/dr.kirti-vedio.jpg"
                                      alt="Dr. Kirti Chadha — Chief Scientific & Innovation Officer, Metropolis (video thumbnail)"
                                      className="img-fluid rounded-top"
                                      loading="lazy"
                                      width={640}
                                      height={360}
                                    />
                                    <div className="video-play-icon position-absolute top-50 start-50 translate-middle">
                                      <i className="fa fa-play text-dark fs-6"></i>
                                    </div>
                                  </a>
                                </div>
                                <h6 className="text-light semi">
                                  Dr. Kirti Chadha
                                </h6>
                                <p className="thumb-p text-light mb-0">
                                  Chief Scientific & Innovation Officer,
                                  Metropolis Healthcare Limited
                                </p>
                              </div>
                            </div>

                            {/* Video Modal */}
                            <Modal
                              show={showVideo}
                              onHide={() => setShowVideo(false)}
                              size="lg"
                              centered
                            >
                              <Modal.Header closeButton>
                                <Modal.Title>Dr. Kirti Chadha</Modal.Title>
                              </Modal.Header>
                              <Modal.Body className="p-0 position-relative">
                                {loading && (
                                  <div className="video-loader-overlay">
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {showVideo && (
                                  <div className="ratio ratio-16x9">
                                    <iframe
                                      src="https://www.youtube.com/embed/QhgUcn_bMI4?autoplay=1"
                                      title="Dr. Kirti Chadha"
                                      allow="autoplay; fullscreen"
                                      allowFullScreen
                                      onLoad={() => setLoading(false)}
                                    ></iframe>
                                  </div>
                                )}
                              </Modal.Body>
                            </Modal>
                          </>

                          {/* Speaker 2 */}
                          <>
                            <div className="col-sm-6">
                              <div className="speaker-card text-light h-100 p-3 rounded-3">
                                <div className="position-relative mb-3">
                                  <a href="#" onClick={handleMp4Open}>
                                    <LazyImg
                                      src="/assets/img/shubnum.jpeg"
                                      alt="Dr. Shubnum Singh — Principal Advisor, Health Policy (video thumbnail)"
                                      className="img-fluid rounded-top"
                                      loading="lazy"
                                      width={640}
                                      height={360}
                                    />
                                    <div className="video-play-icon position-absolute top-50 start-50 translate-middle">
                                      <i className="fa fa-play text-dark fs-6"></i>
                                    </div>
                                  </a>
                                </div>
                                <h6 className="text-light semi">
                                  Dr. Shubnum Singh
                                </h6>
                                <p className="thumb-p text-light mb-0">
                                  Principal Advisor, Health Policy,
                                  Confederation of Indian Industry (CII)
                                </p>
                              </div>
                            </div>

                            {/* Video Modal for Speaker 2 */}
                            <Modal
                              show={showMp4}
                              onHide={() => setShowMp4(false)}
                              size="lg"
                              centered
                            >
                              <Modal.Header closeButton>
                                <Modal.Title>Dr. Shubnum Singh</Modal.Title>
                              </Modal.Header>
                              <Modal.Body className="p-0 position-relative">
                                {loading && (
                                  <div className="video-loader-overlay">
                                    <div
                                      className="spinner-border text-primary"
                                      role="status"
                                    >
                                      <span className="visually-hidden">
                                        Loading...
                                      </span>
                                    </div>
                                  </div>
                                )}
                                {showMp4 && (
                                  <div className="ratio ratio-16x9">
                                    <iframe
                                      src="/assets/img/Dr.shubnum.mp4"
                                      title="Dr. Shubnum Singh"
                                      allow="autoplay; fullscreen"
                                      allowFullScreen
                                      onLoad={() => setLoading(false)}
                                    ></iframe>
                                  </div>
                                )}
                              </Modal.Body>
                            </Modal>
                          </>
                        </div>
                      </div>
                    </div>

                    {/* Quotes carousel */}
                    <Carousel
                      className="quotes-carousel mt-4 d-none"
                      interval={5000}
                      indicators
                      controls={false}
                    >
                      {quotes.map(({ name, role, quote, img }) => (
                        <Carousel.Item key={name}>
                          <figure className="quote-card mx-auto">
                            {img ? (
                              <LazyImg
                                src={img}
                                alt={name}
                                className="quote-photo"
                                width={64}
                                height={64}
                              />
                            ) : (
                              <span className="quote-photo quote-photo--placeholder" aria-hidden="true">
                                <i className="fa fa-user"></i>
                              </span>
                            )}
                            <div>
                              <blockquote className="quote-text mb-2">
                                “{quote}”
                              </blockquote>
                              <figcaption>
                                <span className="quote-name">{name}</span>
                                <span className="quote-role">{role}</span>
                              </figcaption>
                            </div>
                          </figure>
                        </Carousel.Item>
                      ))}
                    </Carousel>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* VOICES SECTION END */}

          {/* AWARDS SECTION START */}
          <section className="awards-section pb-5 d-none">
            <div className="container">
              <h2 className="ul-section-title text-center mb-4">
                Awards and <strong className="green">Recognition</strong>
              </h2>
              <div className="row row-cols-1 row-cols-md-2 g-4 justify-content-center">
                {awards.map(({ title, meta }) => (
                  <div className="col" key={title}>
                    <div className="award-card h-100">
                      <span className="award-icon" aria-hidden="true">
                        <i className="fa fa-trophy"></i>
                      </span>
                      <div>
                        <h3 className="award-title">{title}</h3>
                        <p className="award-meta mb-0">{meta}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>
          {/* AWARDS SECTION END */}

          {/* VIKSIT BHARAT SECTION START */}
          <section className="viksit-section py-5">
            <div className="container">
              <div className="text-center mx-auto viksit-intro">
                <h2 className="ul-section-title">
                  Building the health workforce of a{" "}
                  <strong className="green">Viksit Bharat</strong>
                </h2>
                <p className="ul-section-descr">
                  A developed India needs a healthy India, and a healthy India
                  needs a skilled, well-distributed health workforce. MedEngage
                  invests in the education, research ability and readiness of
                  tomorrow's doctors.
                </p>
              </div>
              <div className="row row-cols-1 row-cols-md-3 g-3 mt-2">
                {pillars.map(({ icon, title, descr }) => (
                  <div className="col" key={title}>
                    <div className="pillar-tile h-100">
                      <i className={`${icon} pillar-icon`} aria-hidden="true"></i>
                      <h3 className="pillar-title">{title}</h3>
                      <p className="pillar-descr mb-0">{descr}</p>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-center thumb-p mt-4 mb-2">
                Aligned with the National Medical Commission's competency-based
                medical education, NEP 2020, Ayushman Bharat and the National
                Health Mission, and UN SDGs 3, 4, 5 and 10.
              </p>
              {/* TODO: swap these badges for official programme and SDG logos */}
              <ul className="alignment-row list-unstyled mb-0">
                {alignments.map((name) => (
                  <li className="alignment-chip" key={name}>
                    {name}
                  </li>
                ))}
                {sdgs.map(({ num, color }) => (
                  <li
                    className="alignment-chip alignment-chip--sdg"
                    key={num}
                    style={{ backgroundColor: color }}
                  >
                    SDG {num}
                  </li>
                ))}
              </ul>
            </div>
          </section>
          {/* VIKSIT BHARAT SECTION END */}

          {/* JURY SECTION START */}
          <section className="ul-team ul-section-spacing" id="jury">
            <div className="ul-container">
              <div className="ul-section-heading justify-content-center">
                <div className="left text-center">
                  <h2 className="text-center ul-section-title mb-2 main-heading">
                    <span className="green">Jury</span> and Summit
                  </h2>
                  <p className="ul-section-descr mb-4">
                    Our scholars are selected by an independent jury of senior
                    clinicians and academics.
                  </p>
                </div>
              </div>

              <div className="row g-2 g-sm-3 g-md-4 row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 ul-team-row justify-content-center">
                {teamMembers.slice(0, showCount).map((member, index) => (
                  <div className="col" key={index}>
                    <div className="ul-team-member">
                      <div className="ul-team-member-img">
                        <LazyImg
                          src={member.img}
                          alt={`${member.name} — ${member.desc}`}
                          loading="lazy"
                          width={300}
                          height={300}
                        />
                      </div>
                      <div className="ul-team-member-info text-center p-3">
                        <h3 className="ul-team-member-name mb-2">
                          <Link
                            to="/team-details"
                            className="text-decoration-none text-dark"
                          >
                            {member.name}
                          </Link>
                        </h3>
                        <p className="thumb-p mb-3 doctor-info">
                          {member.desc}
                        </p>
                        {/* Link opens modal */}
                        <a
                          href="#!"
                          className="thumb-p green"
                          data-bs-toggle="modal"
                          data-bs-target="#teamModal"
                          onClick={() => setSelectedMember(member)}
                          aria-label={`Read more about ${member.name}, ${member.role}`}
                        >
                          Read More
                        </a>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Meet the jury expands the full list in place */}
              <div className="ul-banner-btns d-flex flex-wrap justify-content-center gap-2 w-100 p-4">
                {showCount < teamMembers.length ? (
                  <button
                    className="btn btn-success"
                    onClick={() => setShowCount(teamMembers.length)}
                  >
                    Meet the jury
                  </button>
                ) : (
                  <button
                    className="btn btn-success"
                    onClick={() => setShowCount(4)}
                  >
                    Show less
                  </button>
                )}
                <Link to="/summit-2024" className="btn btn-outline-success">
                  MedEngage Summit
                </Link>
              </div>
            </div>

            {/* Modal */}
            <div
              className="modal fade"
              id="teamModal"
              tabIndex="-1"
              aria-labelledby="teamModalLabel"
              aria-hidden="true"
            >
              <div className="modal-dialog modal-lg modal-dialog-centered">
                <div className="modal-content">
                  <div className="modal-header bgg-light text-success">
                    <h3 className="modal-title" id="teamModalLabel">
                      {selectedMember?.name}
                    </h3>
                    <button
                      type="button"
                      className="btn-close"
                      data-bs-dismiss="modal"
                      aria-label="Close"
                    ></button>
                  </div>
                  <div className="modal-body d-flex flex-column flex-md-row gap-3">
                    <LazyImg
                      src={selectedMember?.img}
                      alt={`${selectedMember?.name} — ${selectedMember?.desc}`}
                      className="rounded member-img"
                      loading="lazy"
                      width={300}
                      height={300}
                    />
                    <div className="modal-info flex-grow-1 text-start mt-3 mt-md-0">
                      <p className="fw-semibold mb-3 text-success">
                        {selectedMember?.desc}
                      </p>
                      {selectedMember?.moreInfo?.length > 0 && (
                        <ul className="list-unstyled mb-0">
                          {selectedMember.moreInfo.map((point, index) => (
                            <li key={index} className="mb-2">
                              <i className="fa fa-hand-o-right small-icon me-2 text-success"></i>
                              {point}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>
        </main>
        <SignIn
          show={activeModal === "signIn"}
          handleClose={() => setActiveModal(null)}
          openSignUp={() => setActiveModal("signUp")}
          openReset={() => setActiveModal("reset")}
          onLoginSuccess={handleLoginSuccess}
        />
        <ResetPasswordModal
          show={activeModal === "reset"}
          handleClose={() => setActiveModal(null)}
          handleBackToSignIn={() => setActiveModal("signIn")}
        />
        <Signup
          show={activeModal === "signUp"}
          onClose={() => setActiveModal(null)}
          openSignIn={() => setActiveModal("signIn")}
          onSignupSuccess={handleLoginSuccess}
        />

        {/* FOOTER SECTION START */}

        {/* FOOTER SECTION END */}
      </div>
    </>
  );
};

export default Home;
